import { z } from 'zod';
import { weeklyHoursSchema } from './booking.js';
import { emailSchema } from './common.js';

/**
 * Members of a paid workspace.
 *
 * A member is invited by email, signs in to their own account, and joins. They
 * keep their own calendars, working hours and token health — see
 * `docs/workspace-members.md` for why that shape was chosen over staff records
 * inside one account.
 */
export const memberRoleSchema = z.enum(['agent']);
export type MemberRole = z.infer<typeof memberRoleSchema>;

export const workspaceMemberSchema = z.object({
  /** The membership, which is what invitations and removal act on. */
  id: z.string(),
  /**
   * The member's own user id, and null until they accept.
   *
   * Distinct from `id` because they answer different questions: an event type
   * names the *person* whose diary it uses, while resending or removing acts on
   * the membership. Naming somebody who has not joined is impossible, which is
   * exactly what a null here says.
   */
  agentId: z.string().nullable(),
  email: z.string(),
  /** Null until they accept: there is no account to take a name from yet. */
  name: z.string().nullable(),
  role: memberRoleSchema,
  status: z.enum(['invited', 'active', 'removed']),
  /**
   * How the membership ended, for the ones that have. Null while it is live.
   *
   * "Yuki left" and "you removed Yuki" both write the same status, and they are
   * not the same thing to anybody reading the list a month later.
   */
  endedBy: z.enum(['owner', 'member', 'account_deleted']).nullable().default(null),
  removedAt: z.string().nullable().default(null),
  /**
   * Whether this member's diary can be consulted, and why not when it cannot.
   *
   * `none` is the one worth showing. A member with no calendar connected is not
   * refused — plenty of people work off the shared page and nothing else — but
   * their bookings reach no calendar at all, which is invisible unless the
   * screen says so. `blocked` is a connection that has stopped working, which
   * drops them out of the rotation until they fix it.
   */
  calendar: z.enum(['ok', 'none', 'blocked']).default('ok'),
  /**
   * The meeting kinds this person can actually produce.
   *
   * Three of the location kinds depend on the individual: Zoom needs their own
   * Zoom account, and Meet and Teams are minted by the calendar write, so they
   * need the right provider as their write target. Sent so the event type
   * editor can say which names are unusable for the location being chosen, at
   * the moment it is being chosen.
   */
  provides: z
    .object({ zoom: z.boolean(), googleMeet: z.boolean(), teams: z.boolean() })
    .default({ zoom: false, googleMeet: false, teams: false }),
  /**
   * The hours this person has said they work for this business.
   *
   * What they set in Business Box, not what their calendar happens to say — it
   * is the standing arrangement, which is the thing an owner wants to confirm
   * when they are working out who covers the mornings. Null until they join,
   * and when they have no hours set at all.
   */
  hours: z
    .object({
      /** Their name for it, which is how they will talk about it. */
      scheduleName: z.string(),
      timezone: z.string(),
      weekly: weeklyHoursSchema,
    })
    .nullable()
    .default(null),
  invitedAt: z.string(),
  acceptedAt: z.string().nullable(),
  /** Null once accepted, because the invitation has been spent. */
  invitationExpiresAt: z.string().nullable(),
});
export type WorkspaceMember = z.infer<typeof workspaceMemberSchema>;

export const inviteMemberInputSchema = z.object({ email: emailSchema });
export type InviteMemberInput = z.infer<typeof inviteMemberInputSchema>;

/** A workspace from the member's own side, which is the one list that crosses accounts. */
/**
 * What a member has set for one workspace.
 *
 * All three sit together because they answer the same question — how this
 * person does the job for this business — and because that is the one screen a
 * member has.
 */
export const memberSettingsSchema = z.object({
  /** Null means "whichever I have"; the owner may have required one anyway. */
  preferredVideo: z.enum(['zoom', 'google_meet', 'teams']).nullable().default(null),
  phone: z.string().trim().max(40).nullable().default(null),
  note: z.string().trim().max(500).nullable().default(null),
  /**
   * Which of this person's own schedules this business gets.
   *
   * Null means their usual one, which is what everybody had before this
   * existed. Somebody taking bookings for two businesses can work mornings for
   * one and afternoons for the other without blocking anything out — the
   * business's own hours still bound it, so this can only narrow what they
   * offer, never widen it.
   */
  scheduleId: z.string().nullable().default(null),
});
export type MemberSettings = z.infer<typeof memberSettingsSchema>;

export const workspaceMembershipSchema = z.object({
  id: z.string(),
  ownerName: z.string(),
  joinedAt: z.string(),
  settings: memberSettingsSchema,
  /**
   * Which video tools this person could actually use, so the screen offers only
   * those and does not let somebody choose a tool that silently does nothing.
   */
  canUse: z.object({ zoom: z.boolean(), googleMeet: z.boolean(), teams: z.boolean() }),
});
export type WorkspaceMembership = z.infer<typeof workspaceMembershipSchema>;

/**
 * What the invitation page may show before anyone has signed in.
 *
 * Who sent it, and the address it was sent to. The address is already in the
 * hands of whoever opened the link, and naming it is what lets the page say
 * which account to sign in as instead of leaving them guessing.
 */
export const invitationPreviewSchema = z.object({
  ownerName: z.string(),
  email: z.string(),
});
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

/**
 * What a member is actually offering, and actually doing.
 *
 * Fetched separately from the member list because each row costs a calendar
 * read: the list should appear at once and these should fill in behind it.
 */
export const memberLoadSchema = z.object({
  id: z.string(),
  /**
   * Minutes rather than hours, all three of them.
   *
   * The screen formats them; a decimal would already have lost the difference
   * between half past and thirty-six minutes past, which is exactly the kind of
   * number people ask about.
   */
  declaredMinutes: z.number(),
  /** Those minutes minus their own calendar and every booking they already hold. */
  freeMinutes: z.number(),
  /** How many of them this workspace's bookings take. */
  bookedMinutes: z.number(),
  /** How many appointments those hours are. */
  bookings: z.number(),
  /** False when their calendar could not be read, so `freeHours` is a guess. */
  readable: z.boolean(),
});
export type MemberLoad = z.infer<typeof memberLoadSchema>;
